import React, { useState } from 'react'
import { Select } from 'antd'
import { FormActionFormActionScheduleUnitNameChoices } from '@/__gen/types'
import { Input } from '@/components/common/inputs'
import style from './common.module.sass'

type Cycle = { unitName: FormActionFormActionScheduleUnitNameChoices; unitAmount: number }
export type NullableCycle = {
	unitName: FormActionFormActionScheduleUnitNameChoices
	unitAmount: number | null
}

enum PrefillChoices {
	Weekly = 'Weekly',
	Fortnightly = 'Fortnightly',
	Monthly = 'Monthly',
	Quarterly = 'Quarterly',
	SixMonthly = 'Six Monthly',
	Yearly = 'Yearly',
	Custom = 'Custom',
}

const ALLOWED_VALUES = {
	[FormActionFormActionScheduleUnitNameChoices.Days]: 365,
	[FormActionFormActionScheduleUnitNameChoices.Months]: 18,
}

const CYCLE_MAP_PRE: (Cycle & { label: PrefillChoices })[] = [
	{
		unitName: FormActionFormActionScheduleUnitNameChoices.Days,
		unitAmount: 7,
		label: PrefillChoices.Weekly,
	},
	{
		unitName: FormActionFormActionScheduleUnitNameChoices.Days,
		unitAmount: 14,
		label: PrefillChoices.Fortnightly,
	},
	{
		unitName: FormActionFormActionScheduleUnitNameChoices.Months,
		unitAmount: 1,
		label: PrefillChoices.Monthly,
	},
	{
		unitName: FormActionFormActionScheduleUnitNameChoices.Months,
		unitAmount: 3,
		label: PrefillChoices.Quarterly,
	},
	{
		unitName: FormActionFormActionScheduleUnitNameChoices.Months,
		unitAmount: 6,
		label: PrefillChoices.SixMonthly,
	},
	{
		unitName: FormActionFormActionScheduleUnitNameChoices.Months,
		unitAmount: 12,
		label: PrefillChoices.Yearly,
	},
]

export const getStringFromCycle = (cycle: Cycle): string => {
	return `${cycle.unitName}|${cycle.unitAmount}`
}

export const CYCLE_MAP = CYCLE_MAP_PRE.reduce(
	(map, currentItem) => {
		const key = getStringFromCycle(currentItem)
		map[key] = currentItem
		return map
	},
	{} as { [s: string]: (typeof CYCLE_MAP_PRE)[number] },
)

export const PREFILL_CHOICE_MAP = CYCLE_MAP_PRE.reduce(
	(map, currentItem) => {
		map[currentItem.label] = currentItem
		return map
	},
	{} as { [p in PrefillChoices]: (typeof CYCLE_MAP_PRE)[number] },
)

export const humanReadableCycle = (cycle: Cycle): string => {
	return (
		CYCLE_MAP[getStringFromCycle(cycle)]?.label || `Every ${cycle.unitAmount} ${cycle.unitName.toLowerCase()}`
	)
}

const getPrefill = (value: NullableCycle) => {
	return (
		(value.unitAmount !== null &&
			CYCLE_MAP[getStringFromCycle({ unitName: value.unitName, unitAmount: value.unitAmount })]?.label) ||
		PrefillChoices.Custom
	)
}

const limitAmount = ({
	unitName,
	unitAmount,
}: {
	unitName: FormActionFormActionScheduleUnitNameChoices
	unitAmount: number
}) => {
	return Math.min(unitAmount, ALLOWED_VALUES[unitName])
}

export const CycleInput = ({
	value,
	onChange,
}: {
	value: NullableCycle
	onChange: (cycle: NullableCycle) => void
}) => {
	const [prefillChoice, setPrefillChoice] = useState<PrefillChoices>(getPrefill(value))
	const options: { value: PrefillChoices; label: string }[] = [
		...Object.entries(CYCLE_MAP).map(([key, value], i) => ({
			value: value.label,
			label: value.label,
		})),
		{
			value: PrefillChoices.Custom,
			label: PrefillChoices.Custom,
		},
	]

	const onSelectPrefill = (prefillChoice: PrefillChoices) => {
		setPrefillChoice(prefillChoice)
		if (prefillChoice === PrefillChoices.Custom) {
			onChange({ unitName: FormActionFormActionScheduleUnitNameChoices.Months, unitAmount: null })
			return
		}
		onChange(PREFILL_CHOICE_MAP[prefillChoice])
	}

	const onUnitAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let unitAmount = parseInt(e.target.value)
		if (!unitAmount) {
			onChange({ ...value, unitAmount: null })
			return
		}
		onChange({ ...value, unitAmount: limitAmount({ unitName: value.unitName, unitAmount }) })
	}

	const onUnitNameChange = (v: FormActionFormActionScheduleUnitNameChoices) => {
		onChange({ unitAmount: limitAmount({ unitName: v, unitAmount: value.unitAmount ?? 0 }), unitName: v })
	}
	return (
		<div>
			<Select
				style={{ width: 134 }}
				// Using onSelect instead of onChange because onChange is not called when the value is the same
				// which creates weird UX when custom is selected, you set it to 6 months, then reselect the prefill for 6 months
				// (I expect the custom inputs to hide)
				onSelect={onSelectPrefill}
				value={getPrefill(value)}
				options={options}
			/>
			{prefillChoice === PrefillChoices.Custom && (
				<div className={style.customContainer}>
					<Input
						type="number"
						value={value.unitAmount ?? ''}
						className={style.numberInput}
						onChange={onUnitAmountChange}
						autoFocus
					/>
					<Select
						style={{ width: 92 }}
						onChange={onUnitNameChange}
						value={value.unitName}
						options={Object.values(FormActionFormActionScheduleUnitNameChoices).map((v) => ({
							value: v,
							label: v.charAt(0).toUpperCase() + v.slice(1).toLowerCase(),
						}))}
					/>
				</div>
			)}
		</div>
	)
}
